import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, Renderer2 } from "@angular/core";
import { AnchorUISref, UISref, UISrefActive } from "@uirouter/angular";
import { IMenu } from "app/types/menu";
import { Subject, distinctUntilChanged, filter, map } from "rxjs";

@Component({
    selector: 'li[opMenuItem]',
    templateUrl: './opMenuItem.view.html',
    imports: [CommonModule, AnchorUISref, UISref, UISrefActive],
    providers: [{
            provide: 'accordionProvider',
            useValue: new Subject<Element[]>()
        }]
})
export class OpMenuItem implements OnInit, AfterViewInit, OnDestroy {

  private readonly _status = new Map<string, boolean>();
  private readonly _changeSubject = new Subject<void>();
  private readonly _subscription = this._changeSubject
    .pipe(map(() => this._getStatus()), distinctUntilChanged())
    .subscribe((result) => this._updateStatus(result));

  private _currentStatus: boolean = false;
  private _submenuActive: boolean = false;

  protected _item: IMenu;
  protected _uiSref: string = "";
  protected _hasSubmenu: boolean = false;
  protected _submenuFixed: boolean = false;

  @Input('opMenuItem') set item(value: IMenu) {
    this._item = value;
    this._hasSubmenu = value.submenu?.length > 0;
    this._uiSref = value.stateName ? value.stateName : value.link;
  };

  @Input() activeCss: string = "active";
  @Input() useIcon: boolean = true;
  @Input() menuAccordionMode: boolean = false;
  @Output() uiSrefStatus = new EventEmitter<{ active: boolean }>();

  @Input() set submenuFixed(value: boolean) {
    this._submenuFixed = value;
    if(this._hasSubmenu && value) this._setSubmenuActive(true);
    else if(this._hasSubmenu && !this._currentStatus) {
      this._setSubmenuActive(false);
    }
  }

  constructor(
    private _liHost: ElementRef<HTMLElement>,
    private _rnd: Renderer2,
    @Inject('accordionProvider') private _accordionSubject: Subject<Element[]>
  ) {}

  ngOnInit(): void {
    if(this._hasSubmenu) {
      this._rnd.addClass(this._liHost.nativeElement, 'submenu');
      this._accordionSubject.asObservable()
        .pipe(filter((value) => this._submenuActive &&  value.includes(this._liHost.nativeElement)))
        .subscribe(() => this._setSubmenuActive(false));
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this._item.submenu && this._currentStatus) {
        this._submenuActive = true;
        this._rnd.addClass(this._liHost.nativeElement, 'submenu-active');
      }
    });
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _getStatus() {
    let result = false;
    for(let item of this._status) {
      result = item[1];
      if(result) break;
    }
    return result;
  }

  private _updateStatus(result: boolean) {
    if(result) this._rnd.addClass(this._liHost.nativeElement, this.activeCss);
    else this._rnd.removeClass(this._liHost.nativeElement, this.activeCss);
    this._currentStatus = result;
    this.uiSrefStatus.emit({ active: result });
  }

  statusChanged(id: string, value: boolean) {
    this._status.set(id, value);
    this._changeSubject.next();
  }

  submenuToggle($event: MouseEvent) {
    $event.preventDefault();
    if(this._submenuFixed) return;
    if (this._submenuActive) this._setSubmenuActive(false);
    else {
      this._setSubmenuActive(true);
      setTimeout(() => this._scrollIntoView($event.target as HTMLElement), 500);
      if (!this.menuAccordionMode) return;
      const siblings = Array.from(this._liHost.nativeElement.parentElement.children);
      const result = siblings.filter(ele => ele != this._liHost.nativeElement && ele.classList.contains('submenu'));
      this._accordionSubject.next(result)
    }
  }

  private _setSubmenuActive(value: boolean) {
    this._submenuActive = value;
    if (value) {
      this._rnd.addClass(this._liHost.nativeElement, 'submenu-active');
    }
    else {
      this._rnd.removeClass(this._liHost.nativeElement, 'submenu-active');
    }
  }

  private _scrollIntoView(target: HTMLElement) {
    target.scrollIntoView({ behavior: 'smooth', block: "center" });
  }
}
